import {
  getFiscalQuarterStartDate,
  isFiscalQuarterInPast,
  parseFiscalQuarterLabel
} from '../../../../utils/time';

/**
 * Milestone date categories for sorting
 */
type DateCategory = 'DATE' | 'TBD' | 'NA';

/**
 * Base milestone interface with required fields for sorting
 */
interface SortableMilestone {
  name: string;
  date?: string | null;
  reviewType?: string | null;
  status?: string | null;
}

/**
 * Month names for parsing month-year formats
 */
const MONTH_NAMES = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec'
] as const;

const MONTH_NAMES_LONG = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december'
] as const;

/**
 * Determine milestone date category for sorting
 * Priority: reviewType > date value > status
 */
export const getMilestoneCategory = (
  milestone: SortableMilestone
): DateCategory => {
  // PRIORITY 1: Check reviewType first (most explicit indicator)
  if (
    milestone.reviewType === 'Not Required' ||
    milestone.reviewType === 'Milestone Not Required'
  ) {
    return 'NA';
  }

  if (milestone.reviewType === 'TBD') {
    return 'TBD';
  }

  // PRIORITY 2: Check date value
  if (milestone.date === 'N/A') {
    return 'NA';
  }

  if (!milestone.date || milestone.date === '' || milestone.date === 'TBD') {
    return 'TBD';
  }

  // PRIORITY 3: Check status
  if (milestone.status === 'TBD') {
    return 'TBD';
  }

  // Valid date - has a date value and reviewType is not TBD/Not Required
  return 'DATE';
};

/**
 * Parse various date formats and return timestamp for comparison
 * Supports: month-year (JAN 2026), quarters (Q1 2026), standard dates (2026-01-15)
 */
export const parseMilestoneDate = (dateStr: string): number => {
  if (!dateStr) return 0;

  // Handle month-year formats: "JAN 2026", "February 2026"
  const monthYearMatch = dateStr.match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (monthYearMatch) {
    const monthStr = monthYearMatch[1].toLowerCase();
    const year = parseInt(monthYearMatch[2], 10);

    // Find month index (0-11)
    let monthIndex = MONTH_NAMES.indexOf(monthStr as any);

    if (monthIndex === -1) {
      monthIndex = MONTH_NAMES_LONG.indexOf(monthStr as any);
    }

    if (monthIndex !== -1) {
      return new Date(year, monthIndex, 1).getTime();
    }
  }

  // Handle quarter formats: "Q1 2026"
  // Use the FIRST month of the quarter for sorting purposes
  const quarterMatch = dateStr.match(/^Q([1-4])\s+(\d{4})$/);
  if (quarterMatch) {
    const quarterStartDate = getFiscalQuarterStartDate(dateStr);
    return quarterStartDate ? quarterStartDate.getTime() : 0;
  }

  // Handle standard date formats: "2026-01-15"
  // IMPORTANT: Parse ISO date WITHOUT timezone conversion to avoid date shifts
  const isoDateMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;
    // Use Date constructor with year, month (0-indexed), day to avoid timezone issues
    return new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day)
    ).getTime();
  }

  // Fallback for other formats
  try {
    const timestamp = new Date(dateStr).getTime();
    return isNaN(timestamp) ? 0 : timestamp;
  } catch {
    return 0;
  }
};

/**
 * Check if a milestone date is in the past
 * For month/quarter formats, the entire period must be past
 * For specific dates, compare against today
 */
export const isMilestoneDateInPast = (dateStr: string): boolean => {
  if (!dateStr) return false;

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth(); // 0-11

  // Handle month-year formats: "JAN 2026", "February 2026"
  const monthYearMatch = dateStr.match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (monthYearMatch) {
    const monthStr = monthYearMatch[1].toLowerCase();
    const year = parseInt(monthYearMatch[2], 10);

    // Find month index (0-11)
    let monthIndex = MONTH_NAMES.indexOf(monthStr as any);
    if (monthIndex === -1) {
      monthIndex = MONTH_NAMES_LONG.indexOf(monthStr as any);
    }

    if (monthIndex !== -1) {
      // Check if the entire month is past
      // Month is past only if: year is past OR (same year AND month is past)
      if (year < currentYear) return true;
      if (year === currentYear && monthIndex < currentMonth) return true;
      return false; // Current month or future
    }
  }

  // Handle quarter formats: "Q1 2026"
  const quarterMatch = dateStr.match(/^Q([1-4])\s+(\d{4})$/);
  if (quarterMatch) {
    return isFiscalQuarterInPast(dateStr, today);
  }

  // Handle standard date formats: "2026-01-15"
  // IMPORTANT: Parse ISO date WITHOUT timezone conversion to avoid date shifts
  const isoDateMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;
    // Use Date constructor with year, month (0-indexed), day to avoid timezone issues
    const milestoneDate = new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day)
    );
    if (isNaN(milestoneDate.getTime())) return false;

    // Set both dates to start of day for fair comparison
    today.setHours(0, 0, 0, 0);
    milestoneDate.setHours(0, 0, 0, 0);

    return milestoneDate < today;
  }

  // Fallback for other date formats
  try {
    const milestoneDate = new Date(dateStr);
    if (isNaN(milestoneDate.getTime())) return false;

    // Set both dates to start of day for fair comparison
    today.setHours(0, 0, 0, 0);
    milestoneDate.setHours(0, 0, 0, 0);

    return milestoneDate < today;
  } catch {
    return false;
  }
};

/**
 * Date granularity levels for sorting
 */
type DateGranularity = 'SPECIFIC_DATE' | 'MONTH' | 'QUARTER';

/**
 * Determine the granularity of a date string
 */
export const getDateGranularity = (dateStr: string): DateGranularity => {
  if (!dateStr) return 'MONTH'; // Default

  // Check for month-year format: "JAN 2026", "February 2026"
  if (/^[A-Za-z]+\s+\d{4}$/.test(dateStr)) {
    return 'MONTH';
  }

  // Check for quarter format: "Q1 2026"
  if (/^Q[1-4]\s+\d{4}$/.test(dateStr)) {
    return 'QUARTER';
  }

  // Everything else is treated as a specific date
  return 'SPECIFIC_DATE';
};

/**
 * Compare two dates by granularity
 * More specific dates come first: SPECIFIC_DATE < MONTH < QUARTER
 */
const compareByGranularity = (
  granularityA: DateGranularity,
  granularityB: DateGranularity
): number => {
  const granularityOrder = {
    SPECIFIC_DATE: 0,
    MONTH: 1,
    QUARTER: 2
  };
  return granularityOrder[granularityA] - granularityOrder[granularityB];
};

/**
 * Extract the time period (year-month) from a date string
 * Returns [year, month] where month is 0-11
 */
const extractTimePeriod = (dateStr: string): [number, number] => {
  if (!dateStr) return [0, 0];

  // Handle month-year formats: "JAN 2026", "December 2025"
  const monthYearMatch = dateStr.match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (monthYearMatch) {
    const monthStr = monthYearMatch[1].toLowerCase();
    const year = parseInt(monthYearMatch[2], 10);

    let monthIndex = MONTH_NAMES.indexOf(monthStr as any);
    if (monthIndex === -1) {
      monthIndex = MONTH_NAMES_LONG.indexOf(monthStr as any);
    }

    if (monthIndex !== -1) {
      return [year, monthIndex];
    }
  }

  // Handle quarter formats: "Q1 2026"
  // Use the FIRST month of the quarter for period grouping
  const quarterMatch = dateStr.match(/^Q([1-4])\s+(\d{4})$/);
  if (quarterMatch) {
    const quarterInfo = parseFiscalQuarterLabel(dateStr);
    if (quarterInfo) {
      return [quarterInfo.startYear, quarterInfo.startMonthIndex];
    }
  }

  // Handle standard date formats: "2026-01-15" or "2025-12-17"
  try {
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return [date.getFullYear(), date.getMonth()];
    }
  } catch {
    return [0, 0];
  }

  return [0, 0];
};

/**
 * Compare two time periods
 * Returns: negative if a < b, positive if a > b, 0 if equal
 */
const compareTimePeriods = (
  periodA: [number, number],
  periodB: [number, number]
): number => {
  // First compare years
  if (periodA[0] !== periodB[0]) {
    return periodA[0] - periodB[0];
  }
  // Then compare months
  return periodA[1] - periodB[1];
};

/**
 * Compare two milestones by category and date
 * Returns: negative if a < b, positive if a > b, 0 if equal
 */
const compareMilestonesByCategory = (
  categoryA: DateCategory,
  categoryB: DateCategory
): number => {
  if (categoryA === categoryB) return 0;

  // Define sort order: DATE < TBD < NA
  const categoryOrder = { DATE: 0, TBD: 1, NA: 2 };
  return categoryOrder[categoryA] - categoryOrder[categoryB];
};

/**
 * Sort milestones by date with smart granularity handling
 * Primary sort: Chronological order (by time period)
 * Secondary sort (within same time period): specific dates → months → quarters
 * Tertiary sort: preserve original (input array) order — CSME-112707
 */
export const sortMilestonesByDate = <T extends SortableMilestone>(
  milestones: T[]
): T[] => {
  // Capture each milestone's original index so that milestones with an
  // identical date (after all category/period/granularity/exact-date
  // comparisons) fall back to their original input-array order instead of
  // alphabetical order (CSME-112707).
  const indexed = milestones.map((milestone, index) => ({ milestone, index }));

  return indexed
    .sort((entryA, entryB) => {
      const a = entryA.milestone;
      const b = entryB.milestone;
      const categoryA = getMilestoneCategory(a);
      const categoryB = getMilestoneCategory(b);

      // First, sort by category (DATE < TBD < NA)
      const categoryComparison = compareMilestonesByCategory(
        categoryA,
        categoryB
      );
      if (categoryComparison !== 0) {
        return categoryComparison;
      }

      // Within DATE category
      if (categoryA === 'DATE' && categoryB === 'DATE') {
        // Get time periods for chronological comparison
        const periodA = extractTimePeriod(a.date || '');
        const periodB = extractTimePeriod(b.date || '');
        const periodComparison = compareTimePeriods(periodA, periodB);

        // If different time periods, sort chronologically
        if (periodComparison !== 0) {
          return periodComparison;
        }

        // Within same time period, sort by granularity (specific dates → months → quarters)
        const granularityA = getDateGranularity(a.date || '');
        const granularityB = getDateGranularity(b.date || '');
        const granularityComparison = compareByGranularity(
          granularityA,
          granularityB
        );

        if (granularityComparison !== 0) {
          return granularityComparison;
        }

        // Within same time period and granularity, sort by exact date (for specific dates with different days)
        const dateA = parseMilestoneDate(a.date || '');
        const dateB = parseMilestoneDate(b.date || '');

        if (dateA !== dateB) {
          return dateA - dateB;
        }
      }

      // Final tiebreaker: same date (or both TBD/NA with no further
      // distinguishing info) — preserve original input-array order rather
      // than re-sorting alphabetically (CSME-112707).
      return entryA.index - entryB.index;
    })
    .map(entry => entry.milestone);
};

/**
 * Sort milestones by name (alphabetically)
 */
export const sortMilestonesByName = <T extends SortableMilestone>(
  milestones: T[]
): T[] => {
  return [...milestones].sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Generic milestone sort function
 * @param milestones - Array of milestones to sort
 * @param sortBy - Sort method: 'date', 'name', or 'none'
 * @returns Sorted array of milestones
 */
export const sortMilestones = <T extends SortableMilestone>(
  milestones: T[],
  sortBy: 'date' | 'name' | 'none'
): T[] => {
  switch (sortBy) {
    case 'date':
      return sortMilestonesByDate(milestones);
    case 'name':
      return sortMilestonesByName(milestones);
    case 'none':
    default:
      return milestones;
  }
};
